/* eslint-disable react/prop-types */
import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import SelectorComponent from "app/common/SelectorComponent";
import { useSelector } from "react-redux";
import { AiOutlineCalendar, AiOutlineWarning } from "react-icons/ai";
import CardComponent from "app/common/CardComponent";
import clsx from "clsx";
import _ from "lodash";
import ScenarioNameComponent from "app/scenarios/map/constraints/ScenarioNameComponent";
import ScenarioTypeToggle from "./ScenarioTypeToggle";
import ScenarioSummaryCard from "./ScenarioSummaryCard";

const WarningMessage = () => {
  const useStyles = makeStyles((theme) => ({
    root: {
      display: "flex",
      alignItems: "center",
    },
    iconContainer: {
      display: "inherit",
      alignItems: "inherit",
    },
    warning: {
      color: theme.palette.primary.main,
    },
    clampedText: {
      "-webkit-line-clamp": 2,
      "-webkit-box-orient": "vertical",
      display: "-webkit-box",
      overflow: "hidden",
    },
  }));

  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box
        className={clsx(classes.warning, classes.iconContainer)}
        marginRight="5px"
      >
        <AiOutlineWarning size={20} />
      </Box>
      <Typography
        variant="body2"
        className={clsx(classes.warning, classes.clampedText)}
      >
        Prices will be overwritten
      </Typography>
    </Box>
  );
};

const CopyScenarioSelector = ({
  scenarios,
  defaultScenario = {},
  onChange,
}) => {
  const [selectedScenario, setSelectedScenario] = React.useState(
    defaultScenario
  );

  React.useEffect(() => {
    if (!selectedScenario) setSelectedScenario(defaultScenario);
  }, [defaultScenario]);

  const handleScenarioChange = (scenarioOption) => {
    if (selectedScenario.id === scenarioOption.key) return;

    const scenario = _.find(scenarios, ["id", scenarioOption.key]);

    setSelectedScenario(scenario);
    if (onChange) onChange(scenario);
  };

  const getDefaultOptionIndex = () => {
    if (defaultScenario.id)
      return _.findIndex(scenarios, ["id", defaultScenario.id]);
    return 0;
  };

  return (
    <SelectorComponent
      defaultOption={getDefaultOptionIndex()}
      options={_.orderBy(
        _.map(scenarios, (scenario) => {
          return {
            key: scenario.id,
            label: scenario.name,
          };
        }),
        "name",
        "asc"
      )}
      title="Scenarios"
      onChange={handleScenarioChange}
      style={{
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
      }}
    />
  );
};

const useStyles = makeStyles(() => ({
  root: {
    "& > *": {
      marginBottom: "20px",
      "&:last-child": {
        marginBottom: 0,
      },
    },
  },
}));

export const CopyScenarioCard = ({
  title,
  cycles,
  onCycleChange,
  defaultCycle = {},
  onScenarioTypeToggle,
  defaultScenarioType,
  scenarioSummaryCard,
  setScenarios,
  disabledToggleButtons,
}) => {
  const classes = useStyles();
  const scenariosSlice = useSelector((state) => state.scenarios);

  const getDefaultOptionIndex = () => {
    if (defaultCycle.id) return _.findIndex(cycles, ["id", defaultCycle.id]);
    return 0;
  };

  const handleCycleChange = (cycleOption) => {
    const cycle = _.find(cycles, ["id", cycleOption.key]);
    const scenarios = _.reduce(
      scenariosSlice.totalsByScenarioId,
      (flattened, scenarioTotal) => {
        if (scenarioTotal.scenario.cycle.id === cycle.id)
          flattened.push(scenarioTotal.scenario);

        return flattened;
      },
      []
    );

    if (onCycleChange) onCycleChange(cycle);
    if (setScenarios) setScenarios(scenarios);
  };

  return (
    <CardComponent
      body={
        <Box className={classes.root}>
          <Typography variant="body1">{title}:</Typography>
          <SelectorComponent
            icon={<AiOutlineCalendar size={22} />}
            defaultOption={getDefaultOptionIndex()}
            options={_.map(cycles, (cycle) => {
              return {
                key: cycle.id,
                label: cycle.label,
              };
            })}
            title="Cycles"
            onChange={handleCycleChange}
          />
          <ScenarioTypeToggle
            onChange={onScenarioTypeToggle}
            disabledButtons={disabledToggleButtons}
            defaultType={defaultScenarioType}
          />
          {scenarioSummaryCard}
        </Box>
      }
    />
  );
};

export const CopyFromScenarioCard = ({
  onCycleChange,
  onSelectedScenario,
  defaultScenario = {},
}) => {
  const cycleSlice = useSelector((state) => state.cycle);
  const [scenarios, setScenarios] = React.useState([]);
  const [scenarioType, setScenarioType] = React.useState(
    defaultScenario.scenario_type_id === 1 ? "reference" : "custom"
  );
  const [selectedScenario, setSelectedScenario] = React.useState(
    defaultScenario
  );
  const [scenariosReady, setScenariosReady] = React.useState(false);

  const referenceScenario = _.find(scenarios, ["scenario_type_id", 1]);
  const customScenarios = _.filter(scenarios, ["scenario_type_id", 3]);

  const handleScenariosListChange = (scenariosList) => {
    setScenarios(scenariosList);
    setScenariosReady(true);
  };

  React.useEffect(() => {
    if (scenarioType === "reference" && referenceScenario)
      setSelectedScenario(referenceScenario);
  }, [scenarioType, referenceScenario]);

  React.useEffect(() => {
    if (selectedScenario && onSelectedScenario)
      onSelectedScenario(selectedScenario);
  }, [selectedScenario]);

  return (
    <CopyScenarioCard
      title="From"
      cycles={cycleSlice.list}
      defaultCycle={defaultScenario.cycle}
      onCycleChange={onCycleChange}
      onScenarioTypeToggle={setScenarioType}
      setScenarios={handleScenariosListChange}
      defaultScenarioType={scenarioType}
      disabledToggleButtons={
        scenariosReady && {
          custom: !customScenarios.length,
        }
      }
      scenarioSummaryCard={
        <ScenarioSummaryCard
          scenario={selectedScenario}
          header={
            scenarioType === "custom" &&
            !!customScenarios.length && (
              <CopyScenarioSelector
                scenarios={customScenarios}
                onChange={setSelectedScenario}
                defaultScenario={selectedScenario}
              />
            )
          }
          showScenarioName={scenarioType !== "custom"}
        />
      }
    />
  );
};

export const CopyToScenarioCard = ({
  cycles,
  fromScenario,
  onFormChange,
  onCycleChange,
}) => {
  const [scenarioType, setScenarioType] = React.useState("custom");
  const [scenarios, setScenarios] = React.useState([]);
  const referenceScenario = _.find(scenarios, ["scenario_type_id", 1]);
  const [scenarioFormReady, setScenarioFormReady] = React.useState(false);
  const [newScenarioName, setNewScenarioName] = React.useState("");
  const [newScenarioComment, setNewScenarioComment] = React.useState("");
  const [invalidName, setInvalidName] = React.useState(false);
  const [
    invalidcharacterInCommment,
    setInvalidcharacterInCommment,
  ] = React.useState(false);
  const [invalidcharacter, setInvalidcharacter] = React.useState(false);
  const user = useSelector((state) => state.auth.user);

  const getDefaultName = (name) => `Copy of ${name}`;
  const [lastDefaultName, setLastDefaultName] = React.useState(
    getDefaultName(fromScenario.name)
  );

  React.useEffect(() => {
    /**
     * This useEffect will set the scenario name to the default value if:
     *  1- Scenario name is empty
     *  2- Scenario name is the same as the previous deault name (this means that the user
     * did not modified the default name and then we can assume he is not interested in
     * choosing its own name)
     */
    const defaultName = getDefaultName(fromScenario.name);
    if (
      (fromScenario && !newScenarioName) ||
      newScenarioName === lastDefaultName
    ) {
      setNewScenarioName(defaultName);
      setScenarioFormReady(true);
    }
    setLastDefaultName(defaultName);
  }, [fromScenario]);

  React.useEffect(() => {
    if (newScenarioName) {
      setNewScenarioName(newScenarioName.trimStart().replace("  ", " "));
      setInvalidName(_.some(scenarios, ["name", newScenarioName.trim()]));
      setInvalidcharacter(
        !/^[A-Za-z0-9\sÁÉÍÓÚáéíóúÜüÑñ-]+$/.test(newScenarioName.trim())
      );
    }
  }, [newScenarioName, scenarios]);

  React.useEffect(() => {
    if (newScenarioComment) {
      setInvalidcharacterInCommment(
        newScenarioComment.trim().includes("|") ||
          newScenarioComment.trim().includes(",")
      );
    }
  }, [newScenarioComment, scenarios]);

  React.useEffect(() => {
    if (onFormChange && !_.isEmpty(user))
      onFormChange({
        valid: !(
          !newScenarioName ||
          (scenarioType === "custom" &&
            (invalidcharacterInCommment || invalidcharacter || invalidName)) ||
          (scenarioType === "reference" &&
            fromScenario.id === referenceScenario.id)
        ),
        scenario:
          scenarioType === "reference"
            ? referenceScenario
            : {
                name: newScenarioName.trim(),
                comment: newScenarioComment,
                owner_label: user.attributes?.email,
                owner_id: user.attributes?.sub,
                scenario_type_id: scenarioType === "reference" ? 1 : 3,
              },
      });
  }, [
    invalidName,
    invalidcharacter,
    invalidcharacterInCommment,
    newScenarioName,
    newScenarioComment,
    scenarioType,
    referenceScenario,
    fromScenario,
  ]);

  return (
    <CopyScenarioCard
      title="To"
      cycles={cycles}
      defaultCycle={fromScenario.cycle}
      onScenarioTypeToggle={setScenarioType}
      onCycleChange={onCycleChange}
      setScenarios={setScenarios}
      scenarioSummaryCard={
        <ScenarioSummaryCard
          scenario={
            scenarioType === "reference"
              ? referenceScenario
              : { owner_label: user?.attributes?.email }
          }
          body={
            scenarioType === "reference" ? (
              <WarningMessage />
            ) : (
              scenarioFormReady && (
                <ScenarioNameComponent
                  scenarioTotal={{ scenario: fromScenario }}
                  newScenarioName={newScenarioName}
                  setNewScenarioName={setNewScenarioName}
                  newScenarioComment={newScenarioComment}
                  setNewScenarioComment={setNewScenarioComment}
                  tightLayout
                  invalidName={invalidName}
                  invalidcharacterInCommment={invalidcharacterInCommment}
                  invalidcharacter={invalidcharacter}
                />
              )
            )
          }
          showScenarioName={scenarioType === "reference"}
          footerStyles={{ borderTop: scenarioType === "custom" && "none" }}
        />
      }
    />
  );
};
